/* =========================================================================
 * WHAT COUNTS AS AN SR-OWNED URL — written ONCE, mirrored in the extension.
 *
 * ⚠️ THE DEFECT THIS CLOSES, 2026-09-18. Brian attached a mark from his
 * device in the new Bezel Drop level, pressed "Render the Bezel Drop sheet in
 * my GPT", and got:
 *
 *     The references did not attach (job-crest.png: not an SR-owned URL).
 *
 * The file was owned. The upload had already succeeded, the bytes were already
 * in HubSpot File Manager, and the URL was the real thing. What refused it was
 * the extension's own allowlist:
 *
 *     [a-z0-9-]+\.hubspotusercontent(-na1)?\.(net|com)\/
 *
 * `[a-z0-9-]+` cannot match a dot, so that pattern allows EXACTLY ONE label in
 * front of `hubspotusercontent`. SR's File Manager host has two — the portal id
 * and the farm:
 *
 *     51263144.fs1.hubspotusercontent-na1.net
 *      ^^^^^^^^ ^^^
 *
 * Evidence this is SR's real host, independent of the bug report:
 *   · tools/concept-probe-375.mjs pins a live crest at
 *     51263144.fs1.hubspotusercontent-na1.net/hubfs/51263144/SR%20College%20Logos/…
 *   · tools/crest-property-find.mjs records the same host in prose as the
 *     established owned-crest location.
 *
 * ⚠️ AND THE MANIFEST ALREADY DISAGREED WITH THE CODE. host_permissions grants
 * `https://*.hubspotusercontent-na1.net/*`, and a Chrome match pattern's `*`
 * DOES span multiple labels. So the extension had permission to fetch the file
 * and then refused to, on a stricter rule nobody had reconciled with the first
 * one. Two allowlists, one concept, drifting apart — the same shape of defect
 * as the duplicated build namespaces in buildApi.js.
 *
 * ⚠️ THE FIX IS NOT TO LOOSEN THE RULE. The rule is what keeps an unowned
 * external asset off a build, and it still refuses a blob:, a data:, a Canva
 * signed link, an openai.com URL and anybody else's host. What changed is that
 * it now matches the hosts SR actually uses, at any subdomain depth and in any
 * HubSpot region. A file that is genuinely not owned is still refused — and the
 * caller's job is to UPLOAD it and then proceed, never to wave it through.
 *
 * THE SOURCE STRING IS EXPORTED so verify-gpt-bridge.mjs can assert that the
 * extension's copy is byte-identical to this one. Two allowlists that must
 * agree, pinned by a test rather than by memory.
 * ========================================================================= */

/**
 * ⚠️ KEEP IN SYNC with tools/sr-gpt-bridge-extension/background.js#OWNED_URL.
 * verify-gpt-bridge.mjs fails the build if these two strings differ.
 */
export const OWNED_URL_SOURCE =
  '^https://(app\\.srchamp\\.com|([a-z0-9-]+\\.)*hubspotusercontent(-[a-z0-9]+)?\\.(net|com))/|^http://localhost:5173/'

export const OWNED_URL = new RegExp(OWNED_URL_SOURCE, 'i')

/** Is this a URL SR hosts — this site, or HubSpot File Manager? */
export function isOwnedAssetUrl(url) {
  return OWNED_URL.test(String(url || '').trim())
}

/**
 * WHY a URL was refused, in words somebody can act on.
 *
 * "not an SR-owned URL" is a true sentence that tells the reader nothing: it
 * names neither what the URL actually is nor what to do next. Each branch below
 * names the thing it found and the move that fixes it.
 */
export function unownedReason(url) {
  const u = String(url || '').trim()
  if (!u) return 'no file was supplied — attach the mark first.'
  if (/^blob:/i.test(u)) {
    return 'the mark is still only in this browser (a blob: URL), which means the upload to HubSpot File Manager did not finish. Attach it again and wait for "Owned in HubSpot File Manager" before rendering.'
  }
  if (/^data:/i.test(u)) {
    return 'the mark is raw bytes in the page (a data: URL) and was never uploaded. Attach it again so it lands in HubSpot File Manager first.'
  }
  if (/^https?:\/\//i.test(u)) {
    let host = u
    try { host = new URL(u).host } catch { /* keep the raw string */ }
    return `it is hosted on ${host}, which is not SR. Only this site and HubSpot File Manager count as owned, because a link to somebody else's server is a dead link waiting to happen. Download the image and attach it from your device so SR hosts its own copy.`
  }
  return `"${u.slice(0, 120)}" is not a URL SR can host from. Attach the mark from your device instead.`
}

/** The unowned references in a job, each with its reason. Empty means good to send. */
export function unownedReferences(refs) {
  return (Array.isArray(refs) ? refs : [])
    .filter((r) => !isOwnedAssetUrl(r?.url))
    .map((r) => ({ name: r?.name || 'reference', url: r?.url || '', reason: unownedReason(r?.url) }))
}
